import { holdRecordingAudio } from '../../sound/audioSession'

/** Whether this browser records voice messages: it has MediaRecorder and gives pages a microphone (secure pages only). */
export function canRecordVoice(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder === 'function' && typeof navigator.mediaDevices?.getUserMedia === 'function'
}

/** The microphone, asked for as the mic is tapped. */
export interface MicrophoneRequest {
  stream: Promise<MediaStream>
  /**
   * Web Audio for the level meter, started by the tap itself: Safari only starts sound from a gesture, and one made later
   * stays suspended and hears silence. Null where the browser has no Web Audio.
   */
  audio: AudioContext | null
  /** Nothing will record after all: the microphone and the sound go as soon as the browser gives them. */
  cancel: () => void
}

/** Sound for the level meter, started here and now. */
function startAudio(): AudioContext | null {
  const Context = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Context) return null
  try {
    const audio = new Context()
    void audio.resume().catch(() => undefined)
    return audio
  } catch {
    return null
  }
}

/**
 * Asks for the microphone straight from the tap on the mic, before the recorder has loaded: the browser asks the first time,
 * and Safari only lets a tap start sound. The recorder takes the stream once it's there.
 */
export function requestMicrophone(): MicrophoneRequest {
  const releaseAudio = holdRecordingAudio()
  const audio = startAudio()
  let cancelled = false
  let granted: MediaStream | null = null
  const stopTracks = (stream: MediaStream) => {
    for (const track of stream.getTracks()) track.stop()
  }
  const stream = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
  stream.then(
    (given) => {
      if (cancelled) stopTracks(given)
      else granted = given
    },
    () => undefined,
  )
  return {
    stream,
    audio,
    cancel: () => {
      if (cancelled) return
      cancelled = true
      if (granted) stopTracks(granted)
      granted = null
      void audio?.close().catch(() => undefined)
      releaseAudio()
    },
  }
}
